import Button from 'Button';
import { type FC, memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { TutorBotMessageContent } from 'TutorBotMessageContent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-solid-svg-icons/faRefresh';
import { faMessageSlash } from '@fortawesome/pro-regular-svg-icons/faMessageSlash';
import { Text } from 'Text';
import { useTranslation } from 'react-i18next';
import {
    type WeeklyVoiceLimitState,
    TUTOR_BOT_BUTTON_HEIGHT_NUM_12_5,
    useWeeklyVoiceTimeLimitState,
} from 'TutorBotConversation';
import { Tooltip } from 'Tooltip';
import { type TranscribedAudioMessage } from '../../TutorBotVoiceAgent.types';
import { getVoiceAgentMessages } from '../../redux/voiceAgent';
import { BottomAlignedWithButton } from '../BottomAlignedWithButton';
import './locales/TutorBotVoiceAgent/PostDiscussion-en.json';
import 'TutorBotChat/locales/chat/chat-en.json';

type AllowRestartConfig = {
    allowRestartDiscussion: true;
};

type DoNotAllowRestartConfig = {
    allowRestartDiscussion?: false;
    buttonText: string;
    action: () => void;
};
type PostDiscussionButtonConfig = AllowRestartConfig | DoNotAllowRestartConfig;

type Props = {
    conversationId: string;
    restartDiscussion: () => void;
    postDiscussionCard?: {
        headerText: string;
        bodyParagraphs: string[];
    };
    postDiscussionButtonConfig: PostDiscussionButtonConfig;
    onCardDismiss?: () => void;
};

// Maybe this logic should be inside of TutorBotMessageContent, but that's kinda tricky
// when it is rendering `children` rather than text
function maybeWrapInQuotes(message: TranscribedAudioMessage) {
    return message.role === 'human' ? `"${message.content}"` : message.content;
}

const TranscriptMessages = ({ transcript }: { transcript: TranscribedAudioMessage[] }) =>
    transcript.map(message => (
        <TutorBotMessageContent
            className="first:mt-auto"
            key={message.id}
            isHumanMessage={message.role === 'human'}
            contentType={message.contentType}
        >
            {maybeWrapInQuotes(message)}
        </TutorBotMessageContent>
    ));

const TranscriptNotAvailable = ({ text }: { text: string }) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-y-4.5">
        <FontAwesomeIcon size="4x" className="text-slate-grey-200" icon={faMessageSlash} />
        <Text>{text}</Text>
    </div>
);

const buttonStyles = {
    height: TUTOR_BOT_BUTTON_HEIGHT_NUM_12_5,
    radius: 'full',
} as const;

function RestartDiscussionButtonWithOptionalTooltip({
    restartDiscussion,
    weeklyVoiceTimeLimitState,
    openTooltip = undefined, // storybook only
}: {
    restartDiscussion: () => void;
    weeklyVoiceTimeLimitState: WeeklyVoiceLimitState;
    openTooltip?: boolean;
}) {
    const { allowedMinutes, refreshDayOfWeek, refreshHour, overTimeLimit } = weeklyVoiceTimeLimitState || {};
    const { t } = useTranslation('back_royal');

    return (
        <Tooltip
            content={t('chat.chat.you_have_used_your_voice_time', {
                allowedMinutes,
                refreshDayOfWeek,
                refreshHour,
            })}
            multiline
            isDisabled={!overTimeLimit}
            isOpen={openTooltip}
        >
            {/*  If we don't have this span wrapping the button, then the fact that the button is disabled
            will prevent the tooltip from working */}
            <span className="inline-block">
                <Button
                    {...buttonStyles}
                    color="slate-grey"
                    variant="bordered-white"
                    onPress={restartDiscussion}
                    icon={<FontAwesomeIcon icon={faRefresh} />}
                    isDisabled={overTimeLimit}
                >
                    {t('TutorBotVoiceAgent.PostDiscussion.restartDiscussion')}
                </Button>
            </span>
        </Tooltip>
    );
}

function ShowTranscript({ conversationId }: { conversationId: string }) {
    const { t } = useTranslation('back_royal', {
        keyPrefix: 'TutorBotVoiceAgent.PostDiscussion',
    });
    const transcript = useSelector(getVoiceAgentMessages(conversationId));

    return (
        <>
            {transcript.length ? (
                <TranscriptMessages transcript={transcript} />
            ) : (
                <TranscriptNotAvailable text={t('transcriptNotAvailable')} />
            )}
        </>
    );
}

export const PostDiscussionPresentation: FC<
    Props & {
        weeklyVoiceTimeLimitState: WeeklyVoiceLimitState;
        openTooltip?: boolean;
    }
> = ({
    conversationId,
    restartDiscussion,
    weeklyVoiceTimeLimitState,
    postDiscussionButtonConfig,
    postDiscussionCard,
    onCardDismiss,
    openTooltip = undefined, // storybook only
}) => {
    const [postDiscussionCardDismissed, setPostDiscussionCardDismissed] = useState(false);

    const handleCardDismiss = useCallback(() => {
        setPostDiscussionCardDismissed(true);
        onCardDismiss?.();
    }, [onCardDismiss]);

    return (
        <div className="h-full w-full">
            {postDiscussionCard &&
                !postDiscussionCardDismissed && (
                    // Top gradient for scroll haze while the card is visible
                    <div className="pointer-events-none absolute left-0 top-0 z-10 h-87.5 w-screen bg-gradient-to-b from-[#F7F7F7] to-transparent sm:h-80" />
                )}

            {/* Bottom gradient for scroll haze */}
            <div className="pointer-events-none absolute bottom-0 left-0 z-10 h-32 w-screen bg-gradient-to-t from-[#F7F7F7] to-transparent" />

            <BottomAlignedWithButton
                type="custom-main"
                // Transcript is scrollable behind the bottom button and must be full viewport height
                fullHeight
                cardHeader={postDiscussionCard?.headerText}
                cardTexts={postDiscussionCard?.bodyParagraphs}
                cardWidth={500}
                cardDismissible
                onCardDismiss={handleCardDismiss}
                Main={<ShowTranscript conversationId={conversationId} />}
                Button={
                    postDiscussionButtonConfig.allowRestartDiscussion ? (
                        <RestartDiscussionButtonWithOptionalTooltip
                            weeklyVoiceTimeLimitState={weeklyVoiceTimeLimitState}
                            restartDiscussion={restartDiscussion}
                            openTooltip={openTooltip}
                        />
                    ) : (
                        <Button {...buttonStyles} onPress={postDiscussionButtonConfig.action} color="primary-gradient">
                            {postDiscussionButtonConfig.buttonText}
                        </Button>
                    )
                }
            />
        </div>
    );
};

const PostDiscussionComponent: FC<Props> = props => {
    const weeklyVoiceTimeLimitState = useWeeklyVoiceTimeLimitState();

    return <PostDiscussionPresentation {...props} weeklyVoiceTimeLimitState={weeklyVoiceTimeLimitState} />;
};

export const PostDiscussion = memo(PostDiscussionComponent) as typeof PostDiscussionComponent;
